import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectCountrySetting } from 'state/constants'
import { useNewTranslation } from 'hooks'
import Icon from 'components/Icon/Icon'
import { theme } from 'theme'
import { useField, useFormikContext } from 'formik'
import { PredefinedContext } from './index'
import {
    BaseEditorContainer,
    EditorContentCardContainer,
    PayoutSectionCardContainer,
    AdditionPayoutCardContainer,
} from './styles'
import Text from 'particles/Text'
import BaseDivider from './BaseDivider'
import Field from 'components/Shared/Fields'
import styled from 'particles/themes/styled'
import SetValue from 'components/Shared/Inputs/SetValueInput'
import { TASK_TYPE_ENUM } from 'services/api/constants'
import { EngagementId, EngagementIds } from 'state/tasks'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { getEngagementTranslation, getEngagementPayoutTranslation } from 'components/utils/translationsMap'
import RectangleButton from 'particles/Button/RectangleButton'
import Toggle from 'particles/Toggle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import { snakeCaseToCamelCase } from 'components/utils'
import { generateUuid } from 'services/uuid'

export interface SelectedEngagementConfig {
    value: string
}

interface PayoutEditorProps {
    taskType: TASK_TYPE_ENUM
    className?: string
    taskUuid: string
}

const PayoutEditor: React.FC<PayoutEditorProps> = ({ className, taskType, taskUuid }) => {
    const [t] = useNewTranslation()
    const { setFieldValue } = useFormikContext()
    const [engagementMetricsField] = useField('payouts.engagementMetrics')
    const [payoutField] = useField('payouts')
    const {
        dynamicTaskState,
        isTaskWithEngagementStep,
        addAdditionalPayout,
        taskSettings,
        removeAdditionalPayout,
        campaignBrandCountry,
        isCampaignLive,
    } = useContext(PredefinedContext)

    const countrySetting = useSelector(selectCountrySetting(campaignBrandCountry))
    const hidePayoutToggle = !isTaskWithEngagementStep
    const payoutTypes = taskSettings && taskSettings.config && taskSettings.config.payout_types
    const additionalPayoutIds = dynamicTaskState && dynamicTaskState.additionalPayoutIds
    const oldToggleList = ['like', 'comment', 'tag', 'share', 'view']

    const budgetCurrencySign = useMemo(() => {
        if (!campaignBrandCountry || !countrySetting) return null
        return countrySetting.currency.sign
    }, [campaignBrandCountry, countrySetting])

    const handleClickAdd = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault()
        const uuid = addAdditionalPayout()
        setFieldValue(`payouts.extras.${uuid}` as never, {
            uuid,
            title: null,
            description: null,
            cost: null,
            min: null,
            max: null,
        })
    }
    const handleClickRemove = (idToRemove: string) => (): void => {
        removeAdditionalPayout(idToRemove)
        setFieldValue(`payouts.extras.${idToRemove}` as never, undefined)
    }

    const handleToggle = (isToggled: boolean, payoutTypeId: string): void => {
        if (isToggled) {
            setFieldValue(`payouts.${payoutTypeId}` as never, {
                cost: 1,
                min: 1,
                max: 1,
                uuid: generateUuid(),
            })
        } else {
            setFieldValue(`payouts.${payoutTypeId}` as never, {
                cost: 0,
                min: 0,
                max: 0,
            })
        }
    }

    const selectedEngagementConfigs = engagementMetricsField.value as unknown as SelectedEngagementConfig[]
    const selectedEngagementValues = selectedEngagementConfigs
        ? selectedEngagementConfigs.map(config => config.value)
        : []
    const payoutTypesList = useMemo(() => {
        if (!payoutTypes) {
            return null
        } else {
            return payoutTypes.filter((payoutType: any) => {
                if (
                    hidePayoutToggle &&
                    (payoutType.id == 'view' || payoutType.id == 'base') // handle deprecated SubmitProofIgScreenshot step payout type
                ) {
                    return true
                } else {
                    const selectedEngagement = selectedEngagementValues.includes(payoutType.id)
                    if (payoutType.is_engagement && !selectedEngagement) {
                        return false
                    } else return true
                }
            })
        }
    }, [payoutTypes])
    const basePayoutList = useMemo(() => {
        let returnList = null
        if (!payoutTypesList) {
            return returnList
        } else {
            returnList = payoutTypesList.filter((payoutType: any) => {
                if (payoutType.id === 'base') {
                    return payoutType
                }
            })
        }
        if (!returnList) {
            return null
        }
        return returnList
    }, [payoutTypesList])

    return (
        <BaseEditorContainer className={className}>
            <EditorContentCardContainer>
                <div className="form-title">
                    <Text scale="H4" className="title" color="primary">
                        {t('campaignEditor.createTask.payoutPageTitle')}
                    </Text>
                </div>
                <BaseDivider isFormTitleDivider />
                {basePayoutList &&
                    basePayoutList.map((payoutType: any) => {
                        return (
                            <div key={payoutType.id}>
                                <PayoutSectionCardContainer>
                                    <CampaignEditorInputFieldContainer>
                                        <Field.NumberInputField
                                            title={t('campaignEditor.createTask.enterFixedPayoutHeader')}
                                            subtitle={t('campaignEditor.createTask.addFixedPayoutSubtext')}
                                            data-testid="test-campaign-payout-input"
                                            currencySign={budgetCurrencySign}
                                            country={campaignBrandCountry}
                                            name={`payouts.${payoutType.id}.cost`}
                                            isRequired
                                            decimal
                                            placeholder={'0.00'}
                                            identifier={`${taskUuid}_${fieldIdentifiers.payoutBase}`}
                                        />
                                    </CampaignEditorInputFieldContainer>
                                </PayoutSectionCardContainer>
                                <BaseDivider />
                            </div>
                        )
                    })}
                {![TASK_TYPE_ENUM.PURCHASE_PRODUCT, TASK_TYPE_ENUM.ATTEND_EVENT].includes(taskType) && (
                    <>
                        <div className="extra-payout-description">
                            <Text scale="bodyM700" color="primary">
                                {t('campaignEditor.createTask.enterPayoutEngagementHeader')}
                            </Text>
                            <Text scale="bodyS400" color="secondary">
                                {t('campaignEditor.createTask.enterPayoutEngagementSelectedSubtext')}
                            </Text>
                        </div>
                        {payoutTypesList &&
                            payoutTypesList.map((payoutType: any) => {
                                const hasMinMax = !payoutType.no_counter
                                let payoutTypeId = payoutType.id
                                const isOldTaskEngagement = oldToggleList.includes(payoutType.id)
                                const hasToggle = isOldTaskEngagement
                                    ? true
                                    : hidePayoutToggle
                                      ? false
                                      : payoutType.is_engagement
                                        ? payoutType.is_engagement
                                        : false
                                const isPayoutType = hasToggle
                                    ? Object.values(payoutField.value[payoutType.id]).some(value => Number(value) > 0)
                                    : false
                                if (taskType === TASK_TYPE_ENUM.IG_POST_V02 && payoutTypeId === EngagementId.VIEW) {
                                    payoutTypeId += '_video'
                                }
                                const engagementLabel = EngagementIds.includes(payoutTypeId)
                                    ? getEngagementPayoutTranslation[payoutTypeId as EngagementId]?.(t)
                                    : { perLabel: '', minLabel: '', maxLabel: '' }
                                const payoutTypeIdentifier = snakeCaseToCamelCase(payoutTypeId)
                                const payoutTypeFieldUuid = payoutField.value[payoutType.id].uuid

                                return (
                                    <div key={payoutType.id}>
                                        {hasToggle ? (
                                            <>
                                                <PayoutSectionCardContainer>
                                                    <CampaignEditorInputFieldContainer>
                                                        <Toggle
                                                            isDisabled={isCampaignLive}
                                                            isChecked={isPayoutType}
                                                            handleChange={(): void => {
                                                                const updateValue = !Boolean(isPayoutType)
                                                                handleToggle(updateValue, payoutType.id)
                                                            }}
                                                            isReadOnly={isCampaignLive ? isCampaignLive : undefined}
                                                            label={getEngagementTranslation[
                                                                payoutTypeId as EngagementId
                                                            ](t)}
                                                            identifier={`${taskUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementToggle}`}
                                                        />
                                                    </CampaignEditorInputFieldContainer>
                                                    {isPayoutType && (
                                                        <CampaignEditorInputFieldContainer>
                                                            <Field.NumberInputField
                                                                currencySign={budgetCurrencySign}
                                                                country={campaignBrandCountry}
                                                                name={`payouts.${payoutType.id}.cost`}
                                                                title={engagementLabel?.perLabel}
                                                                decimal
                                                                isRequired
                                                                placeholder={'0.00'}
                                                                identifier={`${payoutTypeFieldUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementCost}`}
                                                            />
                                                        </CampaignEditorInputFieldContainer>
                                                    )}
                                                    {isPayoutType && hasMinMax && (
                                                        <CampaignEditorInputFieldContainer>
                                                            <Field.NumberInputField
                                                                name={`payouts.${payoutType.id}.min`}
                                                                title={engagementLabel?.minLabel}
                                                                country={campaignBrandCountry}
                                                                isRequired
                                                                placeholder={'0'}
                                                                identifier={`${payoutTypeFieldUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementMin}`}
                                                            />
                                                            <Field.NumberInputField
                                                                name={`payouts.${payoutType.id}.max`}
                                                                title={engagementLabel?.maxLabel}
                                                                country={campaignBrandCountry}
                                                                isRequired
                                                                placeholder={'0'}
                                                                identifier={`${payoutTypeFieldUuid}_${payoutTypeIdentifier}_${fieldIdentifiers.engagementMax}`}
                                                            />
                                                        </CampaignEditorInputFieldContainer>
                                                    )}
                                                </PayoutSectionCardContainer>
                                                <BaseDivider />
                                            </>
                                        ) : (
                                            <></>
                                        )}
                                    </div>
                                )
                            })}
                    </>
                )}
                {additionalPayoutIds && (
                    <div>
                        <Text scale="bodyS500" color="primary" className="other-payout-title">
                            {t('campaignEditor.createTask.addOtherPayoutHeader')}
                        </Text>
                        <Text scale="bodyS400" color="secondary" className="addition-sub-title">
                            {t('campaignEditor.createTask.addOtherPayoutSubtext')}
                        </Text>
                        <div>
                            {additionalPayoutIds &&
                                additionalPayoutIds.length > 0 &&
                                additionalPayoutIds.map((id: string) => {
                                    return (
                                        <AdditionPayoutCardContainer key={id} className="addition-block">
                                            <div className="field-title">
                                                <Text scale="bodyM700" color="primary">
                                                    {t('campaignEditor.createTask.addOtherPayoutHeader')}
                                                </Text>
                                                {!isCampaignLive && <Icon.CloseIcon onClick={handleClickRemove(id)} />}
                                            </div>
                                            <SetValue name={`payouts.extras.${id}.uuid`} value={id} />
                                            <CampaignEditorInputFieldContainer>
                                                <Field.TextInputField
                                                    label={t('campaignEditor.createTask.enterTitleHeader')}
                                                    name={`payouts.extras.${id}.title`}
                                                    width="100%"
                                                    isRequired
                                                    identifier={`${id}_${fieldIdentifiers.extraPayoutTitle}`}
                                                />
                                                <Field.TextAreaField
                                                    label={t('campaignEditor.createTask.enterDescriptionHeader')}
                                                    name={`payouts.extras.${id}.description`}
                                                    width="100%"
                                                    isRequired
                                                    identifier={`${id}_${fieldIdentifiers.extraPayoutDescription}`}
                                                />
                                                <Field.NumberInputField
                                                    title={t('campaignEditor.createTask.enterPayoutForEachHeader')}
                                                    subtitle={t('campaignEditor.createTask.enterPayoutForEachSubtext')}
                                                    currencySign={budgetCurrencySign}
                                                    country={campaignBrandCountry}
                                                    name={`payouts.extras.${id}.cost`}
                                                    isRequired
                                                    decimal
                                                    placeholder={'0.00'}
                                                    identifier={`${id}_${fieldIdentifiers.extraPayoutCost}`}
                                                />
                                                <Field.NumberInputField
                                                    title={t('campaignEditor.createTask.enterMinimumOtherPayout')}
                                                    name={`payouts.extras.${id}.min`}
                                                    country={campaignBrandCountry}
                                                    isRequired
                                                    placeholder={'0'}
                                                    identifier={`${id}_${fieldIdentifiers.extraPayoutMin}`}
                                                />
                                                <Field.NumberInputField
                                                    title={t('campaignEditor.createTask.enterMaximumOtherPayout')}
                                                    name={`payouts.extras.${id}.max`}
                                                    country={campaignBrandCountry}
                                                    isRequired
                                                    placeholder={'0'}
                                                    identifier={`${id}_${fieldIdentifiers.extraPayoutMax}`}
                                                />
                                            </CampaignEditorInputFieldContainer>
                                        </AdditionPayoutCardContainer>
                                    )
                                })}
                        </div>
                    </div>
                )}
                {
                    <CampaignEditorInputFieldContainer>
                        {(isReadOnly: boolean) =>
                            !isReadOnly &&
                            !isCampaignLive && (
                                <RectangleButton
                                    form="outlined"
                                    size="s"
                                    label={t('button.addPayout')}
                                    onClick={handleClickAdd}
                                    icon="plus"
                                />
                            )
                        }
                    </CampaignEditorInputFieldContainer>
                }
            </EditorContentCardContainer>
        </BaseEditorContainer>
    )
}

export default styled(PayoutEditor)`
    width: 100%;
    min-height: 100%;
    height: fit-content;
    box-sizing: border-box;
    background-color: ${theme.newColors.neutral50};
    .other-payout-title {
        margin-bottom: ${theme.newSpacing.xxxxs};
    }
    .extra-payout-description {
        margin-bottom: ${theme.newSpacing.l};
        max-width: 700px;
        display: flex;
        flex-direction: column;
        gap: ${theme.newSpacing.xxxxs};
    }
    .addition-sub-title {
        margin-bottom: ${theme.newSpacing.m};
    }
    .addition-block:first-child {
        margin-top: 0;
    }
`
